import cn from 'classnames';
import { differenceInHours, intlFormatDistanceWithOptions, isBefore, parseJSON } from 'date-fns/fp';
import { Timestamp } from '#components/atoms/timestamp/Timestamp.tsx';
import { useTime } from '#hooks/useTime.ts';
import styles from './ExpiresAt.module.css';

const THREE_MONTHS_HOURS = 2160;
const ONE_YEAR_HOURS = 8760;

type Props = {
  className?: string;
  expiresAt: string | undefined | null;
  /**
   * Renders the label with the value so its tense can follow the state — "Expires: in 30 days" but
   * "Expired yesterday". Table cells leave it off and let the column header do the labelling.
   */
  withLabel?: boolean;
};

export function ExpiresAt({ className, expiresAt, withLabel = false }: Props) {
  const currentTime = useTime();

  if (!expiresAt) {
    return <span className={className}>{withLabel ? 'Expires: Never' : 'Never'}</span>;
  }

  const parsed = parseJSON(expiresAt);
  const isExpired = isBefore(currentTime, parsed);

  // By default, intlFormatDistance will use `quarters` or `q`, which is an awkward unit.  Let's just force months instead.
  const hours = Math.abs(differenceInHours(parsed, currentTime));
  const forceMonths = hours >= THREE_MONTHS_HOURS && hours <= ONE_YEAR_HOURS;
  const distance = intlFormatDistanceWithOptions({ unit: forceMonths ? 'month' : undefined }, currentTime, parsed);

  return (
    <>
      {withLabel && !isExpired ? 'Expires: ' : null}
      <Timestamp className={cn({ [styles.ExpiresAt___expired]: isExpired }, className)} datetime={expiresAt}>
        {isExpired ? `Expired ${distance}` : distance}
      </Timestamp>
    </>
  );
}
